// ═══════════════════════════════════════════════════════════════════════════
// SudoModal — Modal global de re-auth para Sudo Mode
// SEC-B4 (mayo 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Se renderiza una vez en el árbol (idealmente debajo del SudoProvider en
// App.jsx). Se muestra cuando el SudoProvider abre el modal — el componente
// no recibe props, lee el estado y handlers del context.
//
// El flow visible para el user:
//   1. "Confirmá tu identidad para [reason]"
//   2. Si Google SSO: botón "Continuar con Google" → popup → cierra
//   3. Si email/pass: input password + botón
//   4. Spinner mientras hace reauth + enterSudoMode
//   5. "✓ Modo seguro activado por N minutos" (breve, después cierra)
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useId } from 'react'
import { useSudoMode } from '@core/contexts/SudoContext'
import { auth } from '@core/lib/firebase'
import { useDialogoModal } from '@core/hooks/useDialogoModal'

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  )
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  )
}

function IconGoogle() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24">
      <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

export default function SudoModal() {
  const { _modalState, _handleConfirmIdentity, _handleSubmitTotpCode, _handleCancel } = useSudoMode()
  const { open, reason, minutes, status, error } = _modalState

  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const user = auth.currentUser
  const esGoogle = user?.providerData?.some(p => p.providerId === 'google.com')

  // Reset inputs cuando se cierra
  useEffect(() => {
    if (!open) {
      setPassword('')
      setTotpCode('')
    }
  }, [open])

  // Reset TOTP cuando volvemos a idle (después de un error)
  useEffect(() => {
    if (status === 'idle') setTotpCode('')
  }, [status])

  // Semántica de diálogo, como `Modal` (report 0BQntEBh39kAOyeWa0Ga), y su
  // teclado (report XmVfaj3YByq6M6c9XPmo): mientras reautentica, Escape no
  // cancela, igual que el click en el fondo de abajo.
  const idTitulo = useId()
  const dialogo = useDialogoModal({ abierto: open, onCerrar: _handleCancel, bloqueado: status === 'reauthing' || status === 'entering' })
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={(e) => {
        // Cerrar solo si clickeó el backdrop, no el contenido
        if (e.target === e.currentTarget && status !== 'reauthing' && status !== 'entering') {
          _handleCancel()
        }
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={idTitulo} {...dialogo} className="bg-zinc-900 border border-white/[0.08] rounded-2xl p-6 max-w-md w-full shadow-2xl focus:outline-none">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
            <IconShield />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id={idTitulo} className="text-[16px] font-semibold text-white">
              Confirmá tu identidad
            </h2>
            <p className="text-[12px] text-zinc-400 mt-0.5 leading-relaxed">
              Esta acción es sensible. Por seguridad, vamos a pedirte que confirmes con tu segundo factor.
            </p>
          </div>
        </div>

        {/* Reason */}
        {reason && (
          <div className="mb-4 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Acción</div>
            <div className="text-[13px] text-zinc-200">{reason}</div>
            <div className="text-[11px] text-zinc-500 mt-2">
              Una vez confirmado, vas a poder hacer esta y otras acciones sensibles durante <strong>{minutes} minutos</strong> sin volver a confirmar.
            </div>
          </div>
        )}

        {/* Status: done */}
        {status === 'done' && (
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center gap-2">
            <IconCheck />
            <span className="text-[13px] font-medium">Modo seguro activado por {minutes} min</span>
          </div>
        )}

        {/* Status: totp — Firebase pidió el segundo factor */}
        {status === 'totp' && (
          <>
            <div className="mb-3 text-[11px] text-zinc-500">
              Usuario: <span className="text-zinc-300">{user?.email || '(no auth)'}</span>
            </div>

            <div className="mb-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
              <div className="text-[12px] text-zinc-200 leading-relaxed">
                Abrí tu app de autenticación (Google Authenticator, Authy, etc.) y pegá el código de <strong>6 dígitos</strong> actual.
              </div>
            </div>

            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              className="w-full px-3 py-3 rounded-lg bg-black/30 border border-white/[0.08] text-white text-center text-[20px] font-mono tracking-[0.5em] focus:outline-none focus:border-amber-500/40"
              placeholder="000000"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && totpCode.length === 6) {
                  _handleSubmitTotpCode(totpCode)
                }
              }}
            />

            {error && (
              <div className="mt-3 text-[12px] bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={() => _handleSubmitTotpCode(totpCode)}
                disabled={totpCode.length !== 6}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold text-[13px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Verificar código
              </button>
              <button
                onClick={_handleCancel}
                className="text-[12px] text-zinc-400 hover:text-white py-1.5"
              >
                Cancelar
              </button>
            </div>

            <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
              El código cambia cada 30 segundos. Asegurate que el reloj de tu celular esté sincronizado.
            </p>
          </>
        )}

        {/* Status: idle / reauthing / entering */}
        {(status === 'idle' || status === 'reauthing' || status === 'entering') && (
          <>
            {/* Email del user actual */}
            <div className="mb-3 text-[11px] text-zinc-500">
              Usuario: <span className="text-zinc-300">{user?.email || '(no auth)'}</span>
            </div>

            {/* Si no es Google, pedir password */}
            {!esGoogle && (
              <div className="mb-3">
                <label className="text-[11px] text-zinc-400 block mb-1">Tu contraseña actual</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={status !== 'idle'}
                  className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-amber-500/40 disabled:opacity-50"
                  placeholder="••••••••"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && password && status === 'idle') {
                      _handleConfirmIdentity({ password })
                    }
                  }}
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-3 text-[12px] bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {/* Botones */}
            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={() => _handleConfirmIdentity({ password })}
                disabled={status !== 'idle' || (!esGoogle && !password)}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-[13px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  esGoogle
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-amber-500 hover:bg-amber-400 text-zinc-900'
                }`}
              >
                {esGoogle && <IconGoogle />}
                {status === 'reauthing' && 'Confirmando con Google…'}
                {status === 'entering' && 'Activando modo seguro…'}
                {status === 'idle' && (esGoogle ? 'Continuar con Google' : 'Confirmar')}
              </button>
              <button
                onClick={_handleCancel}
                disabled={status === 'reauthing' || status === 'entering'}
                className="text-[12px] text-zinc-400 hover:text-white py-1.5 disabled:opacity-30"
              >
                Cancelar
              </button>
            </div>

            {/* Helper text */}
            {esGoogle && status === 'idle' && (
              <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
                Se va a abrir un popup de Google. Si Firebase considera que la sesión necesita refresco de 2FA, vas a tener que pegar tu código TOTP después.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
